import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound, redirect, useRouter } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Gauge,
  Users,
  Fuel,
  Cog,
  Ruler,
  Weight,
  ShieldCheck,
  ExternalLink,
  CheckCircle2,
  Expand,
  ArrowUpRight,
} from "lucide-react";
import { fyndanuListingQuery, fyndanuListingsQuery, formatPrice, formatMil, licenseClass } from "@/lib/vehicles";
import { vehicleSlug, vehicleIdSuffix, UUID_RE } from "@/lib/vehicle-slug";
import { FinanceCalculator } from "@/components/FinanceCalculator";
import { LiveViewers } from "@/components/LiveViewers";
import { VehicleLightbox } from "@/components/VehicleLightbox";
import { FavoriteButton } from "@/components/FavoriteButton";
import { buildVehicleExpert } from "@/lib/vehicle-expert";
import { VehicleExpertBlock } from "@/components/VehicleExpertBlock";
import { vehicleIndexable } from "@/lib/vehicle-seo";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";


const BASE = "https://www.akhusbilfritid.se";

export const Route = createFileRoute("/fordon_/$id")({
  loader: async ({ params, context }) => {
    // Gamla UUID-URL:er permanentflyttas till den läsbara SEO-slugen.
    if (UUID_RE.test(params.id)) {
      const res = await context.queryClient.ensureQueryData(fyndanuListingQuery(params.id));
      if (!res.item) throw notFound();
      throw redirect({
        to: "/fordon/$id",
        params: { id: vehicleSlug(res.item) },
        statusCode: 301,
      });
    }
    // Slug → fordon via id-suffixet i slutet av slugen.
    const suffix = vehicleIdSuffix(params.id);
    const all = await context.queryClient.ensureQueryData(fyndanuListingsQuery);
    const item = (all.items ?? []).find((v) => vehicleIdSuffix(v.id) === suffix);
    if (!item) throw notFound();
    const canonical = vehicleSlug(item);
    if (canonical !== params.id) {
      throw redirect({ to: "/fordon/$id", params: { id: canonical }, statusCode: 301 });
    }
    return { item, indexable: vehicleIndexable(item, all.items ?? []) };
  },


  head: ({ params, loaderData }) => {
    const item = loaderData?.item;
    const url = `${BASE}/fordon/${params.id}`;
    if (!item) {
      return {
        meta: [
          { title: "Fordon inte hittat — AK Husbil & Fritid" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const priceStr = item.price ? `${item.price.toLocaleString("sv-SE")} kr` : "Pris på förfrågan";
    // Annonstitlarna innehåller säljfraser ("Leverans i hela Sverige") som äter
    // upp SERP-utrymmet. Skala bort dem så modell + pris alltid syns.
    const cleanName = item.title
      .replace(/[,–—-]?\s*(hem)?leverans i hela sverige\.?/gi, "")
      .replace(/[,–—-]?\s*lev\.? i hela sverige\.?/gi, "")
      .replace(/\s*\b(inkommande)\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .replace(/[\s,–—-]+$/, "")
      .trim();
    const title = buildTitle(`${cleanName} — ${priceStr}`);
    const desc =
      `${item.title}. ${priceStr}.` +
      (item.mileage ? ` ${item.mileage} mil.` : "") +
      (item.fuel ? ` ${item.fuel}.` : "") +
      (item.sleepingSpots ? ` ${item.sleepingSpots} bäddar.` : "") +
      " Kontrollerad hos AK Husbil & Fritid i Kalix. Ring 070-862 97 43.";
    const vehicleLd = {
      "@context": "https://schema.org",
      "@type": item.category === "husbil" ? "Motorhome" : item.category === "husvagn" ? "RVSaleableItem" : "Vehicle",
      name: item.title,
      brand: item.brand ? { "@type": "Brand", name: item.brand } : undefined,
      model: item.model ?? undefined,
      vehicleModelDate: item.year ?? undefined,
      mileageFromOdometer: item.mileage
        ? { "@type": "QuantitativeValue", value: item.mileage * 10, unitCode: "KMT" }
        : undefined,
      fuelType: item.fuel ?? undefined,
      vehicleTransmission: item.transmission ?? undefined,
      numberOfAxles: undefined,
      image: item.images.slice(0, 6),
      description: desc,
      offers: item.price
        ? {
            "@type": "Offer",
            price: item.price,
            priceCurrency: "SEK",
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/UsedCondition",
            businessFunction: "https://schema.org/Sell",
            // Prisgiltighet krävs av Google för Offer; vi håller lagerpriser i 90 dagar.
            priceValidUntil: new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10),
            areaServed: [
              { "@type": "AdministrativeArea", name: "Norrbotten" },
              { "@type": "Country", name: "Sverige" },
            ],
            seller: {
              "@type": "AutoDealer",
              name: "AK Husbil & Fritid",
              telephone: "+46708629743",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Stabsvägen 5",
                postalCode: "952 51",
                addressLocality: "Kalix",
                addressCountry: "SE",
              },
            },
            url,
          }
        : undefined,

    };
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Hem", item: BASE + "/" },
        {
          "@type": "ListItem",
          position: 2,
          name: item.category === "husvagn" ? "Husvagnar" : "Husbilar",
          item: BASE + (item.category === "husvagn" ? "/husvagnar-till-salu" : "/husbilar-till-salu"),
        },
        { "@type": "ListItem", position: 3, name: item.title, item: url },
      ],
    };
    const faqEntries = [
      {
        q: `Är ${item.title} kontrollerad före försäljning?`,
        a: "Ja. Varje fordon fukttestas, gasoltestas och genomgås mekaniskt innan det får plats hos oss i Kalix. Vi berättar både det som är bra och det som behöver göras.",
      },
      {
        q: "Kan ni ordna transport av fordonet till min hemort?",
        a: "Ja, vi ordnar transport i hela Sverige mot fast pris. Vi lämnar offert när vi vet leveransadress.",
      },
      {
        q: "Går det att finansiera köpet?",
        a: "Ja, vi samarbetar med Wasa Kredit och kan lämna ett förhandsbesked snabbt. Använd finanskalkylatorn på sidan för att räkna på månadskostnad.",
      },
      (item.totalWeight
        ? {
            q: `Vilket körkort krävs för ${item.title}?`,
            a:
              item.totalWeight <= 3500
                ? `Totalvikt ${item.totalWeight} kg — vanligt B-körkort räcker.`
                : item.totalWeight <= 4250
                ? `Totalvikt ${item.totalWeight} kg — B96 eller BE krävs.`
                : `Totalvikt ${item.totalWeight} kg — C1-behörighet krävs.`,
          }
        : null),
      {
        q: "Kan jag byta in min nuvarande husbil eller husvagn?",
        a: "Absolut. Ring 070-862 97 43 eller mejla info@akhusbilfritid.se med bilder och registreringsnummer så återkommer vi med värdering.",
      },
    ].filter(Boolean) as { q: string; a: string }[];

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqEntries.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    return {
      meta: [
        { title },
        { name: "description", content: desc.slice(0, 158) },
        {
          name: "robots",
          content: loaderData?.indexable
            ? "index, follow, max-image-preview:large, max-snippet:-1"
            : "noindex, follow",
        },
        { property: "og:type", content: "product" },
        { property: "og:title", content: item.title },
        { property: "og:description", content: desc.slice(0, 158) },
        { property: "og:url", content: url },
        { property: "og:image", content: item.image },
        { property: "product:price:amount", content: item.price?.toString() ?? "" },
        { property: "product:price:currency", content: "SEK" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: item.image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(vehicleLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
        { type: "application/ld+json", children: JSON.stringify(faqLd) },
      ],
    };
  },
  component: VehicleDetail,
  errorComponent: ({ error }) => {
    const router = useRouter();
    return (
      <div className="container-edge py-20 text-center">
        <p className="text-lg">Något gick fel: {error instanceof Error ? error.message : String(error)}</p>
        <button onClick={() => router.invalidate()} className="mt-4 rounded-full bg-primary px-5 py-2 text-primary-foreground">
          Försök igen
        </button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="container-edge py-20 text-center">
      <h1 className="font-display text-4xl">Fordonet finns inte längre</h1>
      <p className="mt-3 text-muted-foreground">Det kan ha blivit sålt eller flyttats. Se aktuellt lager istället.</p>
      <Link to="/husbilar-till-salu" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-primary-foreground">
        <ArrowLeft size={16} /> Tillbaka till lagret
      </Link>
    </div>
  ),
});

function VehicleDetail() {
  const { id } = Route.useParams();
  const { data: allData } = useSuspenseQuery(fyndanuListingsQuery);
  const suffix = vehicleIdSuffix(id);
  const item = allData.items.find((v) => vehicleIdSuffix(v.id) === suffix)!;
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);


  const lic = licenseClass(item.totalWeight);
  const mil = formatMil(item.mileage);
  const expert = useMemo(() => buildVehicleExpert(item), [item]);
  const backTo = item.category === "husvagn" ? "/husvagnar-till-salu" : "/husbilar-till-salu";

  const similar = useMemo(() => {
    const others = allData.items.filter((x) => x.id !== item.id && x.category === item.category);
    const scored = others.map((o) => {
      let s = 0;
      if (item.brand && o.brand === item.brand) s += 3;
      if (item.price && o.price) {
        const diff = Math.abs(o.price - item.price) / item.price;
        if (diff < 0.25) s += 2;
        else if (diff < 0.5) s += 1;
      }
      if (item.year && o.year && Math.abs(o.year - item.year) <= 3) s += 1;
      return { o, s };
    });
    return scored.sort((a, b) => b.s - a.s).slice(0, 3).map((x) => x.o);
  }, [allData.items, item]);

  return (
    <div className="bg-warm pb-24 md:pb-0">
      {lightbox !== null && (
        <VehicleLightbox
          images={item.images}
          alt={item.title}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}

      <div className="container-edge py-6">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> Tillbaka till lagret
        </Link>
      </div>

      <div className="container-edge grid gap-10 pb-16 lg:grid-cols-[1.4fr_1fr]">
        {/* Gallery */}
        <div>
          <button
            type="button"
            onClick={() => setLightbox(activeImg)}
            className="group relative block w-full overflow-hidden rounded-3xl border border-border bg-secondary"
            aria-label="Öppna bild i helskärm"
          >
            <img
              src={item.images[activeImg]}
              srcSet={imgSrcSet(item.images[activeImg], [600, 900, 1200])}
              sizes="(min-width: 1024px) 60vw, 100vw"
              alt={item.title}
              width={1200}
              height={900}
              fetchPriority="high"
              decoding="async"
              className="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.02]"
            />

            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
              <Expand size={12} /> {item.images.length} bilder
            </span>
          </button>
          {item.images.length > 1 && (
            <div className="mt-3 grid grid-cols-6 gap-2">
              {item.images.slice(0, 12).map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  onDoubleClick={() => setLightbox(i)}
                  className={`overflow-hidden rounded-lg border-2 transition ${i === activeImg ? "border-accent" : "border-transparent opacity-70 hover:opacity-100"}`}
                  aria-label={`Visa bild ${i + 1} av ${item.images.length}`}
                  aria-current={i === activeImg ? "true" : undefined}
                >
                  <img src={src} srcSet={imgSrcSet(src, [200, 400])} sizes="120px" alt="" width={200} height={200} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: pris + CTA */}
        <aside className="space-y-6">
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-wider text-accent">{item.category}</p>
              <div className="flex items-center gap-2">
                <LiveViewers room={`vehicle:${item.id}`} variant="light" />
                <FavoriteButton id={item.id} variant="plain" />
              </div>
            </div>
            <h1 className="mt-2 font-display text-3xl leading-tight md:text-4xl">{item.title}</h1>
            <p className="mt-4 font-display text-4xl text-accent">{formatPrice(item.price)}</p>

            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              {item.year && <Fact icon={<Calendar size={14} />} label="Årsmodell" value={String(item.year)} />}
              {mil && <Fact icon={<Gauge size={14} />} label="Miltal" value={mil} />}
              {item.fuel && <Fact icon={<Fuel size={14} />} label="Bränsle" value={item.fuel} />}
              {item.transmission && <Fact icon={<Cog size={14} />} label="Växellåda" value={item.transmission} />}
              {item.length && <Fact icon={<Ruler size={14} />} label="Längd" value={`${item.length} m`} />}
              {item.totalWeight && <Fact icon={<Weight size={14} />} label="Totalvikt" value={`${item.totalWeight} kg`} />}
              {item.sleepingSpots != null && <Fact icon={<Users size={14} />} label="Sovplatser" value={String(item.sleepingSpots)} />}
              {lic && <Fact icon={<ShieldCheck size={14} />} label="Körkort" value={lic} />}
            </div>

            <div className="mt-6 space-y-2">
              <Link
                to="/kontakt"
                search={{ fordon: item.title, id: item.id }}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Mail size={14} /> Kontakta säljare om denna
              </Link>
              <a
                href="tel:0708629743"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:bg-secondary"
              >
                <Phone size={14} /> Ring 070-862 97 43
              </a>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:bg-secondary"
              >
                <ExternalLink size={14} /> Se på Fyndanu
              </a>
            </div>


            <div className="mt-6 space-y-2 border-t border-border pt-5 text-xs text-muted-foreground">
              <p className="inline-flex items-center gap-2"><MapPin size={12} /> Stabsvägen 5, 952 51 Kalix</p>
              <p className="inline-flex items-center gap-2"><Mail size={12} /> info@akhusbilfritid.se</p>
              <p className="inline-flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Fukttestat & genomgånget före försäljning</p>
              <p className="inline-flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Transport i hela Sverige mot kostnad</p>
              <p className="inline-flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Extra garanti via Autoconcept tillval</p>
            </div>
          </div>

          {item.price && item.price > 50000 && <FinanceCalculator price={item.price} />}
        </aside>
      </div>

      {/* Beskrivning + utrustning */}
      <div className="container-edge grid gap-10 pb-24 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="font-display text-2xl md:text-3xl">Beskrivning</h2>
          <pre className="mt-4 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-foreground/90">
            {item.description}
          </pre>
        </div>
        {item.equipment.length > 0 && (
          <aside>
            <h2 className="font-display text-2xl md:text-3xl">Utrustning</h2>
            <ul className="mt-4 grid gap-1.5 text-sm">
              {item.equipment.map((e, i) => (
                <li key={i} className="inline-flex items-start gap-2">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>

      <VehicleExpertBlock expert={expert} title={item.title} />

      {/* Passar den hos mig? – förifyllt med detta fordon */}
      <div className="container-edge pb-12">
        <div className="mx-auto flex max-w-3xl flex-col items-start gap-4 rounded-3xl border border-accent/30 bg-accent/[0.06] p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <p className="eyebrow text-accent">Gratis verktyg</p>
            <h2 className="mt-1 font-display text-2xl">Får {item.title} plats hos dig?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Mät uppfarten en gång – vi visar planvy i skala, svängen in från gatan och ett
              AR-läge i mobilen. Måtten för det här fordonet är redan ifyllda.
            </p>
          </div>
          <Link
            to="/verktyg/passar-den-hos-mig"
            search={{ v: item.id }}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground transition hover:opacity-90"
          >
            <Ruler size={16} /> Testa måtten
          </Link>
        </div>
      </div>


      {/* FAQ */}
      <div className="container-edge pb-16">
        <div className="mx-auto max-w-3xl rounded-3xl border border-border bg-card p-6 md:p-10">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-1 font-display text-2xl md:text-3xl">Innan du köper</h2>
          <div className="mt-6 divide-y divide-border">
            {[
              { q: `Är ${item.title} kontrollerad före försäljning?`, a: "Ja. Varje fordon fukttestas, gasoltestas och genomgås mekaniskt innan det får plats hos oss i Kalix. Vi berättar både det som är bra och det som behöver göras." },
              { q: "Kan ni ordna transport till min hemort?", a: "Ja, vi ordnar transport i hela Sverige mot fast pris. Vi lämnar offert när vi vet leveransadress." },
              { q: "Går det att finansiera köpet?", a: "Ja, vi samarbetar med Wasa Kredit och kan lämna förhandsbesked snabbt. Räkna på månadskostnad i kalkylatorn ovan." },
              item.totalWeight ? { q: `Vilket körkort krävs för ${item.title}?`, a: item.totalWeight <= 3500 ? `Totalvikt ${item.totalWeight} kg — vanligt B-körkort räcker.` : item.totalWeight <= 4250 ? `Totalvikt ${item.totalWeight} kg — B96 eller BE krävs.` : `Totalvikt ${item.totalWeight} kg — C1-behörighet krävs.` } : null,
              { q: "Kan jag byta in min gamla husbil eller husvagn?", a: "Absolut. Ring 070-862 97 43 eller mejla info@akhusbilfritid.se med bilder och regnr så återkommer vi med värdering." },
            ].filter(Boolean).map((f, i) => (
              <details key={i} className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left font-medium">
                  <span>{(f as { q: string }).q}</span>
                  <span className="text-accent transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{(f as { a: string }).a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* Liknande fordon */}
      {similar.length > 0 && (
        <div className="border-t border-border bg-card/40">
          <div className="container-edge py-16">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
                <p className="eyebrow">Liknande fordon</p>
                <h2 className="mt-1 font-display text-2xl md:text-3xl">Du kanske också gillar</h2>
              </div>
              <Link to={backTo} className="hidden text-sm text-accent hover:underline md:inline">Se hela lagret →</Link>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {similar.map((s) => {
                const smil = formatMil(s.mileage);
                return (
                  <Link
                    key={s.id}
                    to="/fordon/$id"
                    params={{ id: vehicleSlug(s) }}
                    className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                      <img src={s.image} srcSet={imgSrcSet(s.image)} sizes={CARD_SIZES} alt={s.title} width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
                      <div className="absolute right-3 top-3 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur">
                        {formatPrice(s.price)}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="font-display text-lg leading-tight line-clamp-2 group-hover:text-accent">{s.title}</h3>
                      <dl className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {s.year && <div className="inline-flex items-center gap-1"><Calendar size={11} /> {s.year}</div>}
                        {smil && <div className="inline-flex items-center gap-1"><Gauge size={11} /> {smil}</div>}
                        {s.sleepingSpots != null && <div className="inline-flex items-center gap-1"><Users size={11} /> {s.sleepingSpots} bäddar</div>}
                      </dl>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent">
                        Visa fordon <ArrowUpRight size={13} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Sticky CTA på mobil */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur md:hidden">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-muted-foreground">{item.title}</p>
            <p className="font-display text-lg leading-none text-accent">{formatPrice(item.price)}</p>
          </div>
          <a
            href="tel:0708629743"
            aria-label="Ring 070-862 97 43"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-semibold"
          >
            <Phone size={14} /> Ring
          </a>
          <a
            href={`mailto:info@akhusbilfritid.se?subject=${encodeURIComponent(`Intresse: ${item.title}`)}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            <Mail size={14} /> Boka
          </a>
        </div>
      </div>
    </div>
  );
}

function Fact({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl bg-secondary p-3">
      <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">{icon} {label}</p>
      <p className="mt-1 font-medium">{value}</p>
    </div>
  );
}
